/// <amd-module name="Repos/Tfvc/Views/ChangeDetails/Util" />
import { IChangeCollection } from "Repos/ChangesCommon/Contracts";
import { IGitCommitChange } from "Repos/Changes/Contracts";
import { getChangesCollection } from "Repos/Changes/Util";
import { Change, TfvcVersionDescriptor, TfvcVersionOption, TfvcVersionType } from "Repos/Common/Contracts/Git";
import { getParentPath } from "Repos/Common/Util/File";
import { getTfvcVersionString } from "Repos/Common/Util/Version";
import { ITfvcChangeItem } from "./Contracts";

/**
 * Identifies empty folders in the given set of changes.
 *
 * @param changes List of changes.
 */
function getEmptyFolders(changes: Change<ITfvcChangeItem>[]): { [path: string]: boolean } {
    const emptyFoldersMap: { [path: string]: boolean } = {};
    for (const change of changes) {
        const { isFolder, serverItem } = change.item;
        if (isFolder) {
            emptyFoldersMap[serverItem] = true;
        }

        const parentPath = getParentPath(serverItem, "$/") || "";
        // Remove parent path (folder path) from empty paths
        delete emptyFoldersMap[parentPath];
    }

    return emptyFoldersMap;
}

export function getTfvcChangesCollection(
    changes: Change<ITfvcChangeItem>[],
    changeId: string,
    versionType: TfvcVersionType,
    loadMoreChanges?: () => void
): IChangeCollection<TfvcVersionDescriptor> {
    const convertedChanges: IGitCommitChange[] = [];

    // Getting the list of empty folders since they're valid items for TFVC
    // which are displayed as a separate item in a changeset or shelveset
    const emptyFoldersMap = getEmptyFolders(changes);

    // Convert TFVC change to IGitChangeItem
    for (const tfvcChange of changes) {
        const { changeType, item, sourceServerItem } = tfvcChange;
        const { isFolder, isSymLink, serverItem, version } = item;

        convertedChanges.push({
            changeType,
            commitId: version,
            item: {
                isEmptyFolder: emptyFoldersMap[serverItem], // empty folders are allowed for TFVC
                isFolder: isFolder,
                isSymLink: isSymLink,
                path: serverItem,
                originalPath: sourceServerItem,
                trackingId: 0
            }
        });
    }

    const sourceVersion = { version: changeId, versionOption: TfvcVersionOption.None, versionType };
    const targetVersion = { version: changeId, versionOption: TfvcVersionOption.Previous, versionType };
    const changesCollection = getChangesCollection<TfvcVersionDescriptor>(
        convertedChanges,
        "",
        getTfvcVersionString(sourceVersion),
        sourceVersion,
        targetVersion,
        loadMoreChanges,
        `${getTfvcVersionString(sourceVersion)}.${getTfvcVersionString(targetVersion)}.${changes.length}`,
        undefined,
        "$"
    );

    // Tweak changes collection since it's designed for git changes
    const { rootTreeItem } = changesCollection;
    const { childItems = [] } = rootTreeItem;

    changesCollection.rootTreeItem.data.iconProps = { iconName: "TFVCLogo", className: "repos-folder-icon" };
    changesCollection.rootTreeItem.data.name = "$";

    if (childItems.length > 0) {
        for (const tfvcRootItem of childItems) {
            tfvcRootItem.data.name = "$/" + tfvcRootItem.data.name;

            // Since the root item is git-repo item, if it's only one item, we need to make its child the root and eliminate git-repo item
            if (childItems.length === 1) {
                changesCollection.rootTreeItem = tfvcRootItem;
            }

            const { path = "" } = tfvcRootItem.data;
            const pathParts = path.split("/");
            if (pathParts.length <= 2) {
                tfvcRootItem.data.iconProps = { iconName: "TFVCLogo", className: "repos-folder-icon" };
            }
        }
    }

    return changesCollection;
}
